// Derives the dashboard tab-bar / nav-history labels from a PATH. Extracted
// verbatim from partials/Header.js so the derivation is unit-testable — tab
// state is `{id, path}` only (see utils/dashboardTabs.js), so the path is the
// ONLY input a label can be derived from. This function serves every tab in
// the dashboard; changes to one section must leave every other section's
// output byte-identical (dashboardNavRouteMeta.test.js pins that).

export const getDashboardNavPathname = (path) => {
  if (!path || path === '/') return '/';
  const pathname = path.split(/[?#]/)[0];
  return pathname || '/';
};

export const formatDashboardNavSegment = (segment) =>
  segment
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export const getDashboardNavSection = (path) => {
  const [firstSegment] = getDashboardNavPathname(path).split('/').filter(Boolean);
  return firstSegment || '';
};

const DASHBOARD_NAV_ROUTE_META_BY_SECTION = {
  contacts: { list: 'Contacts', entity: 'Contact' },
  forms: { list: 'Forms', entity: 'Form' },
  elements: { list: 'Web Elements', entity: 'Web Element' },
  payments: { list: 'Payments', entity: 'Payment' },
  subscriptions: { list: 'Subscriptions', entity: 'Subscription' },
  payouts: { list: 'Payouts', entity: 'Payout' },
  broadcasts: { list: 'Broadcasts', entity: 'Broadcast' },
  segments: { list: 'Segments', entity: 'Segment' },
  // 'email-blasts' + 'email-templates' removed (PE-1671).
};

const getDashboardNavShortIdentifier = (value) => {
  if (!value || value === 'new' || value === 'edit') return null;
  const decodedValue = decodeURIComponent(value);
  return decodedValue.length > 12 ? decodedValue.slice(0, 8) : decodedValue;
};

export const getDashboardNavRouteMeta = (path) => {
  if (!path || path === '/') return { label: 'Home' };

  const [firstSegment, secondSegment, thirdSegment, fourthSegment] = getDashboardNavPathname(path)
    .split('/')
    .filter(Boolean);

  if (!firstSegment) return { label: 'Home' };

  if (firstSegment === 'settings') {
    // The import WIZARD lives under /settings for routing reasons only — it is
    // a full entity workspace that opens in its own tab (PE-1905/1906), not a
    // Settings tab. The mechanical "<Area> Settings" derivation below produced
    // "Imports New Settings" for it. Every other /settings path keeps the
    // derived form byte-for-byte.
    if (secondSegment === 'imports' && thirdSegment === 'new') {
      return { label: 'New import', badge: 'Imports' };
    }

    const settingsArea = [secondSegment, thirdSegment]
      .filter(Boolean)
      .map(formatDashboardNavSegment)
      .join(' ');

    return {
      label: settingsArea ? `${settingsArea} Settings` : 'Settings',
      badge: settingsArea ? 'Settings' : null,
    };
  }

  const routeMeta = DASHBOARD_NAV_ROUTE_META_BY_SECTION[firstSegment] || {
    list: formatDashboardNavSegment(firstSegment),
    entity: formatDashboardNavSegment(firstSegment),
  };

  if (!secondSegment) return { label: routeMeta.list };

  if (secondSegment === 'new') {
    return {
      label: `New ${routeMeta.entity}`,
      badge: thirdSegment ? formatDashboardNavSegment(thirdSegment) : 'New',
    };
  }

  if (thirdSegment === 'edit') {
    return {
      label: `Edit ${routeMeta.entity}`,
      badge: getDashboardNavShortIdentifier(secondSegment) || 'Edit',
    };
  }

  if (firstSegment === 'forms' && thirdSegment === 'submissions' && fourthSegment) {
    return {
      label: 'Submission Detail',
      badge: getDashboardNavShortIdentifier(fourthSegment) || 'Detail',
    };
  }

  return {
    label: `${routeMeta.entity} Detail`,
    badge: getDashboardNavShortIdentifier(secondSegment) || 'Detail',
  };
};

export const getDashboardNavDestinationLabel = (path) => getDashboardNavRouteMeta(path).label;
